import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, downloadFile } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  AssignmentView, BankAccountView, EmployeeDetail, EmployeeOption, EmployeeSummary,
  OrgChartNode, TimelineEntry, TransferStatus, TransferView,
} from '../types'

/**
 * The directory, flattened for pickers.
 *
 * Rostering and approvals both need "who can I choose from", which is a different
 * question from the paged directory grid — so it asks for one large page and caches it
 * for the session rather than paging inside a dropdown.
 */
export function useEmployeeOptions(size = 500) {
  return useQuery({
    queryKey: ['employees', 'options', size],
    queryFn: async () => {
      const response = await apiClient.get<PageResponse<EmployeeOption>>('/v1/employees', {
        params: { size, sort: 'displayName,asc' },
      })
      return response.data.content
    },
    staleTime: 5 * 60_000,
  })
}

export function useAssignments(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['employees', employeeId, 'assignments'],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<AssignmentView[]>(`/v1/employees/${employeeId}/assignments`)).data,
  })
}

export function useTransfers(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['transfers', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<TransferView[]>('/v1/transfers', { params: { employeeId } })).data,
  })
}

/**
 * Every transfer in the company, optionally narrowed to one status.
 *
 * <p>Distinct from {@link useTransfers}, which answers "what happened to this person". This
 * one answers "what is in flight" — the question somebody running the process has, and the
 * one the endpoint could not answer at all until employeeId became optional.
 */
export function useAllTransfers(status?: TransferStatus | '') {
  return useQuery({
    queryKey: ['transfers', 'all', status],
    queryFn: async () =>
      (await apiClient.get<TransferView[]>('/v1/transfers', {
        params: { status: status || undefined },
      })).data,
    placeholderData: (previous) => previous,
  })
}

export function useTransferMutations() {
  const queryClient = useQueryClient()
  // A transfer changes the assignment timeline the moment it is approved, so both lists
  // are refreshed together rather than leaving the profile showing yesterday's placement.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['transfers'] })
    void queryClient.invalidateQueries({ queryKey: ['employees'] })
  }

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<TransferView>('/v1/transfers', body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TransferView>(`/v1/transfers/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 7 part 5: the directory and the org chart ---

export interface DirectoryParams {
  /** Entity, department and status, sent in the server's filter[field][op] form. */
  legalEntityId?: string
  departmentId?: string
  status?: string
  page?: number
  size?: number
  sort?: string
  q?: string
}

/** The directory. Server-side paged: 1,248 people on dev, and more in production. */
export function useEmployeeDirectory(params: DirectoryParams) {
  return useQuery({
    queryKey: ['employees', 'directory', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<EmployeeSummary>>('/v1/employees', {
        params: {
          page: params.page,
          size: params.size,
          sort: params.sort,
          q: params.q || undefined,
          // The server's filter grammar: filter[field][op]=value, allow-listed on its side.
          ...(params.legalEntityId
            ? { 'filter[legalEntityId][eq]': params.legalEntityId } : {}),
          ...(params.departmentId ? { 'filter[departmentId][eq]': params.departmentId } : {}),
          ...(params.status ? { 'filter[status][eq]': params.status } : {}),
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useOrgChart(depth = 3) {
  return useQuery({
    queryKey: ['employees', 'org-chart', depth],
    queryFn: async () =>
      (await apiClient.get<OrgChartNode[]>('/v1/employees/org-chart', { params: { depth } })).data,
  })
}

// --- Phase 7 part 5: the profile the directory and the org chart both link to ---

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', 'detail', id],
    queryFn: async () => (await apiClient.get<EmployeeDetail>(`/v1/employees/${id!}`)).data,
    enabled: Boolean(id),
  })
}

export function useEmployeeTimeline(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', 'timeline', id],
    queryFn: async () =>
      (await apiClient.get<TimelineEntry[]>(`/v1/employees/${id!}/timeline`)).data,
    enabled: Boolean(id),
  })
}

export function useDirectReports(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', 'reports', id],
    queryFn: async () =>
      (await apiClient.get<EmployeeSummary[]>(`/v1/employees/${id!}/reports`)).data,
    enabled: Boolean(id),
  })
}

// --- CRUD completion, group 1 ---

/**
 * Everything that writes an employee record.
 *
 * <p>One hook rather than three, because they all invalidate the same caches and a screen
 * that adds usually also edits. The invalidation is deliberately broad — a new hire changes
 * the directory, the org chart, the pickers every other module fills from, and the
 * headcount on the dashboard, and a stale picker is how somebody gets assigned to a person
 * who is not there yet.
 */
export function useEmployeeMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['employees'] })
  }

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<EmployeeDetail>('/v1/employees', body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<EmployeeDetail>(`/v1/employees/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Removes a record created in error.
     *
     * <p>Not how an employment ends — that is an exit case, and the server refuses this with
     * the reason when anything at all points at the row. The screen offers it only where the
     * refusal would be the useful answer.
     */
    deactivate: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`/v1/employees/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

export function useBankAccounts(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['employees', employeeId, 'bank-accounts'],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<BankAccountView[]>(`/v1/employees/${employeeId!}/bank-accounts`)).data,
  })
}

export function useBankAccountMutations(employeeId: string | undefined) {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'bank-accounts'] })
  }
  const base = `/v1/employees/${employeeId!}/bank-accounts`

  return {
    save: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<BankAccountView>(`${base}/${id}`, body)).data
          : (await apiClient.post<BankAccountView>(base, body)).data,
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${base}/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

/** The directory as a spreadsheet, carrying whatever the screen is filtered to. */
export function downloadEmployeeExport(params: DirectoryParams) {
  return downloadFile('/v1/employees/export', 'employees.xlsx', {
    q: params.q || undefined,
    ...(params.legalEntityId ? { 'filter[legalEntityId][eq]': params.legalEntityId } : {}),
    ...(params.departmentId ? { 'filter[departmentId][eq]': params.departmentId } : {}),
    ...(params.status ? { 'filter[status][eq]': params.status } : {}),
  })
}
